/* Scroll reveal.
 *
 * A short, slow rise as a band enters the viewport. It is deliberately
 * restrained: 16px and 700ms, once per element, never repeated. Motion on a
 * luxury site should feel like the page settling, not like an animation
 * playing at you.
 *
 * IntersectionObserver, not a scroll listener — a scroll handler reflows on
 * every frame and is the usual reason a "premium" site stutters on a phone.
 */

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');

if (!REDUCED.matches && 'IntersectionObserver' in window) {
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.dataset.revealed = 'true';
        io.unobserve(entry.target);
      }
    },
    /* Fire a little before the band reaches the fold so it has finished
     * settling by the time it is properly in view.
     *
     * The bottom inset is a *positive* margin, not a negative one. At -12%
     * the trigger zone stops 108px above the viewport foot, and an element
     * near the end of the document can never enter it: there is no more page
     * to scroll, so the last band on every page stayed at opacity 0 for
     * good. Measured on /pages/diamond-color, where the final two bands
     * never revealed. A positive bottom margin starts the reveal slightly
     * early instead, which is the effect the negative one was reaching for
     * without the dead zone. */
    { rootMargin: '0px 0px 5% 0px', threshold: 0.01 }
  );

  const watch = () => {
    for (const el of document.querySelectorAll('[data-reveal]:not([data-revealed])')) {
      io.observe(el);
    }
  };

  watch();
  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', watch);

  /* Content that arrives after load has to be picked up too.
   *
   * watch() ran once, so anything swapped in later — the product page's
   * recommendations, a filtered collection grid, a re-rendered cart — kept
   * its `data-reveal` and was never observed, which left it at opacity 0
   * permanently. The rail was invisible on the product page for exactly this
   * reason, and the markup looked correct the whole time.
   *
   * A MutationObserver is the honest fix: it watches for the elements
   * themselves rather than requiring every script that swaps HTML to
   * remember to call back. */
  const mo = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.('[data-reveal]:not([data-revealed])')) io.observe(node);
        for (const el of node.querySelectorAll?.('[data-reveal]:not([data-revealed])') ?? []) {
          io.observe(el);
        }
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
}
